import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { Button, Card, TBody, TD, TH, THead, TR, Table } from '@meshhub/design-tokens/ui';
import { CheckCircle2, Pencil, Plus } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import {
  ActiveBadge,
  ConfirmDialog,
  FormDialog,
  LoadingOrEmpty,
  MutationError,
  SelectField,
  SettingsPage,
  ShowInactiveToggle,
  TextField,
} from '../../components/settings/SettingsKit.web.jsx';
import {
  ADMIN_LOOKUPS_QUERY,
  CREATE_TEMPLATE,
  SET_TEMPLATE_ACTIVE,
  TEMPLATES_QUERY,
  UPDATE_TEMPLATE,
  VALIDATE_PLACEHOLDERS,
} from '../../lib/queries.js';
import { S } from '../../lib/strings.js';
import { ALLOWED_PLACEHOLDERS } from '../../lib/placeholders.js';

/** Stand-in values for the preview pane. Substitution itself is the future
 *  messaging service's job; this only shows the shape of the result. */
const SAMPLE = { firstName: 'علی', lastName: 'احمدی', fullName: 'علی احمدی' };

function renderPreview(body) {
  return (body ?? '').replace(/\{\{\s*([^}]*?)\s*\}\}/g, (match, token) =>
    token in SAMPLE ? SAMPLE[token] : match,
  );
}

export function TemplatesScreen() {
  const [includeInactive, setIncludeInactive] = useState(true);
  const [editing, setEditing] = useState(null);
  const [confirming, setConfirming] = useState(null);

  const variables = { includeInactive };
  const { data, loading } = useQuery(TEMPLATES_QUERY, { variables });
  const channels = useQuery(ADMIN_LOOKUPS_QUERY, {
    variables: { kind: 'COMMUNICATION_CHANNEL', includeInactive: false },
  });
  const categories = useQuery(ADMIN_LOOKUPS_QUERY, {
    variables: { kind: 'TEMPLATE_CATEGORY', includeInactive: false },
  });
  const refetch = [{ query: TEMPLATES_QUERY, variables }];

  const [create, createState] = useMutation(CREATE_TEMPLATE, { refetchQueries: refetch });
  const [update, updateState] = useMutation(UPDATE_TEMPLATE, { refetchQueries: refetch });
  const [setActive, activeState] = useMutation(SET_TEMPLATE_ACTIVE, { refetchQueries: refetch });

  const rows = data?.messageTemplates ?? [];

  return (
    <SettingsPage
      title={S.settings.sections.templates}
      hint={S.settings.templates.sendingElsewhere}
      toolbar={
        <>
          <ShowInactiveToggle checked={includeInactive} onChange={setIncludeInactive} />
          <Button variant="primary" onClick={() => setEditing('new')} className="gap-2">
            <Plus size={15} aria-hidden="true" />
            {S.settings.common.newItem}
          </Button>
        </>
      }
    >
      <MutationError error={createState.error || updateState.error || activeState.error} />
      <LoadingOrEmpty loading={loading && rows.length === 0} empty={!loading && rows.length === 0} />

      {rows.length > 0 && (
        <Table>
          <THead>
            <TR>
              <TH>{S.settings.templates.name}</TH>
              <TH>{S.settings.templates.channel}</TH>
              <TH>{S.settings.templates.category}</TH>
              <TH>{S.settings.templates.body}</TH>
              <TH>{S.settings.common.status}</TH>
              <TH>{S.settings.common.actions}</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((template) => (
              <TR key={template.id} className={template.isActive ? '' : 'opacity-60'}>
                <TD className="font-medium">{template.name}</TD>
                <TD>{template.channel.labelFa}</TD>
                <TD>{template.category.labelFa}</TD>
                <TD className="max-w-sm truncate text-text-muted" title={template.body}>
                  {template.body}
                </TD>
                <TD>
                  <ActiveBadge isActive={template.isActive} />
                </TD>
                <TD>
                  <span className="flex items-center gap-1">
                    <Button variant="ghost" title={S.app.edit} onClick={() => setEditing(template)}>
                      <Pencil size={14} aria-hidden="true" />
                    </Button>
                    <Button variant="ghost" onClick={() => setConfirming(template)}>
                      {template.isActive ? S.app.deactivate : S.app.activate}
                    </Button>
                  </span>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}

      {editing && (
        <TemplateDialog
          template={editing === 'new' ? null : editing}
          channels={channels.data?.lookups ?? []}
          categories={categories.data?.lookups ?? []}
          busy={createState.loading || updateState.loading}
          onClose={() => setEditing(null)}
          onSubmit={async (input) => {
            if (editing === 'new') await create({ variables: { input } });
            else await update({ variables: { id: editing.id, input } });
            setEditing(null);
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(confirming)}
        onOpenChange={(open) => !open && setConfirming(null)}
        title={confirming?.isActive ? S.app.deactivate : S.app.activate}
        message={
          confirming?.isActive
            ? S.settings.common.confirmDeactivate(confirming?.name ?? '')
            : S.settings.common.confirmActivate(confirming?.name ?? '')
        }
        danger={confirming?.isActive}
        confirmLabel={confirming?.isActive ? S.app.deactivate : S.app.activate}
        onConfirm={() =>
          setActive({ variables: { id: confirming.id, isActive: !confirming.isActive } })
        }
      />
    </SettingsPage>
  );
}

function TemplateDialog({ template, channels, categories, onClose, onSubmit, busy }) {
  const client = useApolloClient();
  const [name, setName] = useState(template?.name ?? '');
  const [channelId, setChannelId] = useState(template?.channel.id ?? channels[0]?.id ?? '');
  const [categoryId, setCategoryId] = useState(template?.category.id ?? categories[0]?.id ?? '');
  const [body, setBody] = useState(template?.body ?? '');
  const [validation, setValidation] = useState(null);

  /**
   * Validation runs on the server so the whitelist has exactly one definition.
   * Debounced because it fires per keystroke.
   */
  useEffect(() => {
    if (!body.trim()) {
      setValidation(null);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const { data } = await client.query({
          query: VALIDATE_PLACEHOLDERS,
          variables: { body },
          fetchPolicy: 'network-only',
        });
        setValidation(data.validatePlaceholders);
      } catch {
        setValidation(null); // a transient failure must not block typing
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [body, client]);

  const preview = useMemo(() => renderPreview(body), [body]);
  const allowed = validation?.allowed ?? ALLOWED_PLACEHOLDERS;

  return (
    <FormDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={template ? `${S.app.edit} — ${template.name}` : S.settings.templates.createTitle}
      busy={busy}
      onSubmit={() => onSubmit({ name, channelId, body, categoryId })}
    >
      <TextField
        label={S.settings.templates.name}
        value={name}
        required
        onChange={(event) => setName(event.target.value)}
      />
      <SelectField
        label={S.settings.templates.channel}
        value={channelId}
        onChange={(event) => setChannelId(event.target.value)}
      >
        {channels.map((channel) => (
          <option key={channel.id} value={channel.id}>
            {channel.labelFa}
          </option>
        ))}
      </SelectField>
      <SelectField
        label={S.settings.templates.category}
        value={categoryId}
        onChange={(event) => setCategoryId(event.target.value)}
      >
        {categories.map((category) => (
          <option key={category.id} value={category.id}>
            {category.labelFa}
          </option>
        ))}
      </SelectField>

      <label className="flex flex-col gap-1">
        <span className="text-sm text-text-muted">{S.settings.templates.body}</span>
        <textarea
          value={body}
          required
          rows={4}
          onChange={(event) => setBody(event.target.value)}
          className="rounded border border-border bg-bg p-4 text-base outline-none focus:border-primary"
        />
      </label>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-text-muted">{S.settings.templates.allowedPlaceholders}:</span>
        {allowed.map((placeholder) => (
          <Button
            key={placeholder}
            type="button"
            variant="ghost"
            className="text-xs"
            onClick={() => setBody(`${body}{{${placeholder}}}`)}
          >
            <span dir="ltr">{`{{${placeholder}}}`}</span>
          </Button>
        ))}
      </div>

      {validation && !validation.valid && (
        <ul className="flex flex-col gap-1" role="alert">
          {validation.errors.map((error) => (
            <li key={`${error.placeholder}-${error.position}`} className="text-xs text-danger">
              {error.message}
            </li>
          ))}
        </ul>
      )}
      {validation?.valid && (
        <p className="flex items-center gap-1 text-xs text-success">
          <CheckCircle2 size={12} aria-hidden="true" />
          {S.settings.templates.valid}
        </p>
      )}

      <Card className="bg-bg">
        <span className="text-xs text-text-muted">{S.settings.templates.preview}</span>
        <p className="mt-2 whitespace-pre-wrap text-base">{preview || S.app.none}</p>
      </Card>
    </FormDialog>
  );
}
